import type { ApiClient, DownloadedFile } from '../client';
import {
  QrBatchCreatedSchema,
  QrBatchListSchema,
  QrBatchStatsSchema,
  type QrBatchCreated,
  type QrBatchList,
  type QrBatchStats,
} from '../schemas/qrBatches';

const BASE = '/app_service/panel/v1/qr-batches';

export interface QrBatchPage {
  limit: number;
  offset: number;
}

// The screen reads the first page only: a batch is a few per month, so fifty covers years.
const FIRST_PAGE: QrBatchPage = { limit: 50, offset: 0 };

export async function fetchQrBatches(
  client: ApiClient,
  page: QrBatchPage = FIRST_PAGE,
): Promise<QrBatchList> {
  const query = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
  const data = await client.get<unknown>(`${BASE}?${query.toString()}`);
  return QrBatchListSchema.parse(data);
}

// `202` with the queued batch. The refusals stay `ApiError`s for the host to map:
// `400 invalid_qr_amount`, `409 pending_qr_batch_exists`, `502 qr_generator_unavailable`,
// `403 panel_permission_required`.
export async function requestQrBatch(client: ApiClient, qrAmount: number): Promise<QrBatchCreated> {
  const data = await client.post<unknown>(BASE, { qr_amount: qrAmount });
  return QrBatchCreatedSchema.parse(data);
}

// The batch's CSV, fetched with the token in the Authorization header like any read — never a link
// the browser follows on its own, which would need the token in the URL. The id is a row's string,
// encoded so it can only ever be one path segment.
export async function fetchQrBatchFile(client: ApiClient, id: string): Promise<DownloadedFile> {
  return client.getFile(`${BASE}/${encodeURIComponent(id)}/file`);
}

export async function fetchQrBatchStats(client: ApiClient): Promise<QrBatchStats> {
  const data = await client.get<unknown>(`${BASE}/stats`);
  return QrBatchStatsSchema.parse(data);
}
